import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AddWorkspaceTeamMemberInput,
  WorkspaceTeamGrantsInput,
  WorkspaceTeamInviteItem,
  WorkspaceTeamMemberItem,
  WorkspaceTeamResponse,
} from "@unbox/api";
import { handleError } from "@/api/error";
import { useWorkspaceId } from "@/providers/MerchantProvider";
import { client } from "./client";

export type {
  AddWorkspaceTeamMemberInput,
  WorkspaceTeamGrantsInput,
  WorkspaceTeamInviteItem,
  WorkspaceTeamMemberItem,
  WorkspaceTeamResponse,
};

export type WorkspaceGrants = WorkspaceTeamGrantsInput;

const teamKey = (workspaceId: string | null) => ["workspace-team", workspaceId];

export const useWorkspaceTeam = () => {
  const workspaceId = useWorkspaceId();

  return useQuery<WorkspaceTeamResponse>({
    queryKey: teamKey(workspaceId),
    queryFn: async () => {
      if (!workspaceId) throw new Error("No workspace selected");

      const res = await client.workspace[":workspaceId"].team.$get({ param: { workspaceId } });
      if (!res.ok) throw await handleError(res);
      return (await res.json()) as WorkspaceTeamResponse;
    },
    enabled: !!workspaceId,
  });
};

const useRequiredWorkspaceId = () => {
  const workspaceId = useWorkspaceId();
  return () => {
    if (!workspaceId) throw new Error("No workspace selected");
    return workspaceId;
  };
};

const useInvalidateWorkspaceTeam = () => {
  const workspaceId = useWorkspaceId();
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: teamKey(workspaceId) });
};

export const useAddWorkspaceTeamMember = () => {
  const requireWorkspaceId = useRequiredWorkspaceId();
  const invalidate = useInvalidateWorkspaceTeam();

  return useMutation({
    mutationFn: async (json: AddWorkspaceTeamMemberInput) => {
      const res = await client.workspace[":workspaceId"].team.$post({
        param: { workspaceId: requireWorkspaceId() },
        json,
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: invalidate,
  });
};

export const useUpdateWorkspaceTeamMember = () => {
  const requireWorkspaceId = useRequiredWorkspaceId();
  const invalidate = useInvalidateWorkspaceTeam();

  return useMutation({
    mutationFn: async ({ userId, grants }: { userId: string; grants: WorkspaceGrants }) => {
      const res = await client.workspace[":workspaceId"].team.members[":userId"].$patch({
        param: { workspaceId: requireWorkspaceId(), userId },
        json: grants,
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: invalidate,
  });
};

export const useRemoveWorkspaceTeamMember = () => {
  const requireWorkspaceId = useRequiredWorkspaceId();
  const invalidate = useInvalidateWorkspaceTeam();

  return useMutation({
    mutationFn: async (userId: string) => {
      const res = await client.workspace[":workspaceId"].team.members[":userId"].$delete({
        param: { workspaceId: requireWorkspaceId(), userId },
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: invalidate,
  });
};

export const useUpdateWorkspaceTeamInvite = () => {
  const requireWorkspaceId = useRequiredWorkspaceId();
  const invalidate = useInvalidateWorkspaceTeam();

  return useMutation({
    mutationFn: async ({ inviteId, grants }: { inviteId: string; grants: WorkspaceGrants }) => {
      const res = await client.workspace[":workspaceId"].team.invites[":id"].$patch({
        param: { workspaceId: requireWorkspaceId(), id: inviteId },
        json: grants,
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: invalidate,
  });
};

export const useResendWorkspaceTeamInvite = () => {
  const requireWorkspaceId = useRequiredWorkspaceId();
  const invalidate = useInvalidateWorkspaceTeam();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await client.workspace[":workspaceId"].team.invites[":id"].resend.$post({
        param: { workspaceId: requireWorkspaceId(), id },
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: invalidate,
  });
};

export const useRevokeWorkspaceTeamInvite = () => {
  const requireWorkspaceId = useRequiredWorkspaceId();
  const invalidate = useInvalidateWorkspaceTeam();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await client.workspace[":workspaceId"].team.invites[":id"].revoke.$post({
        param: { workspaceId: requireWorkspaceId(), id },
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: invalidate,
  });
};
